/*
  AAS-4635 retail-only rules — client-side evaluator.

  A product carrying custom.retail_only rules stays visible with its price but
  is not purchasable when a rule matches the current visitor. This file is the
  single evaluation path; Liquid only serializes data (snippets/
  retail-only-data.liquid) and CSS does the hiding (assets/retail-only.css).

  Rule model (mirrors the retail_only_rules metaobject):
    - The metafield holds N rules; rules OR together.
    - A rule is a flat list of conditions: `market:XX` and/or
      `crm_profile_type:X` strings (exact values from the metaobject dropdown).
    - Within a rule: same-type conditions OR together, the two types AND
      together. A type with no conditions is a wildcard (always passes).
      An empty rule ([]) therefore always matches = pure catalog item.
    - Visitor profile comes from tags starting with `crm_profile_type:`.
      D2X and EMP are matched by tag. D2C is matched by EXCLUSION — it means
      "neither D2X nor EMP", so it covers guests, customers with no such tag,
      and customers carrying any other value. A customer tagged both D2C and
      D2X is therefore D2X and does not satisfy a D2C condition.

  Data contract:
    - #retail-only-context — JSON {market, loggedIn, profiles[]} rendered by
      snippets/retail-only-context.liquid (layout/theme.liquid, top of <body>,
      so it precedes every scope).
    - [data-retail-only-scope] — element owning a region (PDP section root,
      quick-view content); its rules sit in a child
      <script type="application/json" data-retail-only-rules-json>.
    - [data-retail-only-rules='…'] — self-contained element (product cards),
      rules inline in the attribute.
    On match the element gets `retail-only--active` (CSS takes over) and
    dispatches a bubbling `retail-only:active` CustomEvent.

  Loading: deferred globally from retail-only-context.liquid (cards, PLPs,
  quick-view via MutationObserver) AND synchronously inside the PDP section
  (zero flash before the ATC region parses). First copy wins — see the
  window.RetailOnly guard. Section Rendering API / facet AJAX swaps re-enter
  through the observer because replacement nodes arrive unclassified.
*/
(function () {
  'use strict';
  if (window.RetailOnly) return;

  var MARKET_PREFIX = 'market:';
  var PROFILE_PREFIX = 'crm_profile_type:';
  var D2C = 'crm_profile_type:D2C';
  var ACTIVE_CLASS = 'retail-only--active';
  var STATE_ATTR = 'data-retail-only-state';

  // D2C is defined by exclusion, not by a tag: a visitor is D2C when they are
  // neither D2X nor EMP. So this list — the non-D2C choices in the
  // retail_only_rules dropdown — is what decides who ISN'T D2C, and it must
  // mirror that dropdown. Add a new privileged profile type to the metaobject
  // definition and it MUST be added here too, or holders of it will keep
  // matching D2C rules.
  var NON_D2C_PROFILES = ['crm_profile_type:D2X', 'crm_profile_type:EMP'];

  var ctx = null;

  function getContext() {
    if (ctx) return ctx;
    var data = { market: '', profiles: [] };
    var el = document.getElementById('retail-only-context');
    if (el) {
      try {
        data = JSON.parse(el.textContent) || data;
      } catch (e) {
        /* malformed context — fall through to the guest defaults above, which
           evaluate as D2C by exclusion */
      }
    }
    ctx = {
      market: MARKET_PREFIX + String(data.market || '').toUpperCase(),
      // Raw crm_profile_type:* tags, not normalised: D2C is derived below
      // rather than substituted in, so an empty list stays an empty list.
      profiles: (data.profiles || []).filter(function (p) {
        return typeof p === 'string' && p.indexOf(PROFILE_PREFIX) === 0;
      })
    };
    return ctx;
  }

  function profileMatches(condition, context) {
    if (condition === D2C) {
      // Anyone who is neither D2X nor EMP: guests, customers with no
      // crm_profile_type tag at all, and customers carrying some other value.
      // Note this beats an explicit D2C tag — a customer tagged both D2C and
      // D2X is D2X, and does NOT satisfy a D2C condition.
      return !context.profiles.some(function (p) {
        return NON_D2C_PROFILES.indexOf(p) !== -1;
      });
    }
    return context.profiles.indexOf(condition) !== -1;
  }

  function ruleMatches(conditions, context) {
    var markets = [];
    var profiles = [];
    (conditions || []).forEach(function (raw) {
      var c = String(raw);
      if (c.indexOf(MARKET_PREFIX) === 0) markets.push(c);
      else if (c.indexOf(PROFILE_PREFIX) === 0) profiles.push(c);
    });
    var marketOk = !markets.length || markets.indexOf(context.market) !== -1;
    var profileOk =
      !profiles.length ||
      profiles.some(function (p) {
        return profileMatches(p, context);
      });
    return marketOk && profileOk;
  }

  function isRetailOnly(rules) {
    if (!Array.isArray(rules) || !rules.length) return false;
    var context = getContext();
    return rules.some(function (rule) {
      return ruleMatches(rule, context);
    });
  }

  function readRules(el) {
    var raw = el.getAttribute('data-retail-only-rules');
    if (raw === null) {
      var json = el.querySelector('script[data-retail-only-rules-json]');
      raw = json ? json.textContent : null;
    }
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function classify(el) {
    if (el.hasAttribute(STATE_ATTR)) return;
    var rules = readRules(el);
    // A scope whose rules JSON hasn't been parsed into the DOM yet (sync run
    // mid-parse) stays unmarked so the deferred pass re-attempts it.
    if (rules === null && el.hasAttribute('data-retail-only-scope')) return;
    var active = isRetailOnly(rules);
    el.setAttribute(STATE_ATTR, active ? 'active' : 'inactive');
    if (!active) return;
    el.classList.add(ACTIVE_CLASS);
    el.dispatchEvent(
      new CustomEvent('retail-only:active', { bubbles: true, detail: { rules: rules } })
    );
  }

  // AAS-5053 renders the retail-only CTAs with `hidden` as their SSR default so
  // they cannot flash visible before the stylesheet that hides them
  // conditionally has loaded. Once a scope is known to be retail-only that
  // default has done its job and has to go: CSS cannot override it, because two
  // author rules reinstate [hidden] with !important at a specificity a scope
  // selector cannot reach —
  //   .product-form__submit.btn[hidden]  (0,3,0, section-main-product.css,
  //                                       inside @media max-width:1024px)
  //   .btn[hidden]                       (0,2,0, theme.css)
  // against `.retail-only--active .retail-only-cta` (0,2,0).
  //
  // This is a document sweep and NOT part of classify() on purpose. The PDP
  // loads this file synchronously inside its section, so classify() runs on the
  // scope while the parser is still above the buy area — the buttons do not
  // exist yet, and the scope is marked classified, so nothing would revisit it.
  // Running here means every later pass (DOMContentLoaded, observer, section
  // load) picks them up whenever they appear.
  function revealActiveCtas() {
    var hiddenCtas = document.querySelectorAll(
      '.' + ACTIVE_CLASS + ' .retail-only-cta[hidden],' +
        '.' + ACTIVE_CLASS + ' .product-tile__label--retail-only[hidden],' +
        '.' + ACTIVE_CLASS + ' .product-tile-v2__label--retail-only[hidden]'
    );
    for (var i = 0; i < hiddenCtas.length; i++) {
      hiddenCtas[i].removeAttribute('hidden');
    }
  }

  function refresh(root) {
    root = root || document;
    if (root.nodeType === Node.ELEMENT_NODE) {
      if (
        root.hasAttribute('data-retail-only-scope') ||
        root.hasAttribute('data-retail-only-rules')
      ) {
        classify(root);
      }
    }
    if (!root.querySelectorAll) return;
    var nodes = root.querySelectorAll(
      '[data-retail-only-scope]:not([' + STATE_ATTR + ']),' +
        '[data-retail-only-rules]:not([' + STATE_ATTR + '])'
    );
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].tagName === 'SCRIPT') continue;
      classify(nodes[i]);
    }
    revealActiveCtas();
  }

  function observe() {
    if (!document.body || observe.installed) return;
    observe.installed = true;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === Node.ELEMENT_NODE) refresh(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.RetailOnly = {
    isRetailOnly: isRetailOnly,
    refresh: refresh,
    getContext: getContext
  };

  refresh(document);
  // The PDP loads a synchronous copy mid-parse for a zero-flash classify of its
  // own scope; installing the observer that early would fire it for every node
  // the parser appends. Post-parse copies (defer) install immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refresh(document);
      observe();
    });
  } else {
    observe();
  }
  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) refresh(event.target);
  });
})();
